import CookieEnum from "constants/CookieEnum";
import IUserCookieModel from "models/common-models/IUserCookieModel";
import { Cookies } from "react-cookie";

const cookies = new Cookies();

interface CookieAttributes {
  path?: string;
  expires?: Date;
  maxAge?: number;
  domain?: string;
  secure?: boolean;
  httpOnly?: boolean;
  sameSite?: "strict" | "lax" | "none";
}

// NOTE (security): The auth token is read by JavaScript in ConfigUtility to build
// the `Authorization: Token ...` header, so this cookie cannot be HttpOnly with the
// current SPA design. We harden it as far as the frontend allows: `secure: true`
// prevents transmission over plaintext HTTP, and `sameSite: "strict"` blocks
// cross-site sends. Residual risk: an XSS can still read the token (CWE-1004).
// The proper fix is a backend-set HttpOnly+Secure+SameSite cookie so the SPA never
// touches the token — tracked as a manual follow-up issue.
// `secure: true` is set unconditionally; on localhost dev over http the cookie will
// not be stored, which is acceptable (dev typically runs over https or uses the API
// session). Adjust per environment if local-http auth is required.
const cookieOptions: CookieAttributes = {
  path: "/",
  maxAge: 604800,
  sameSite: "strict",
  secure: true,
};

const _setCookie = (key: string, value: IUserCookieModel | string): void => {
  cookies.set(key, value, cookieOptions);
};

const _getCookie = (key: string) => {
  return cookies.get(key);
};

export const getCookie = (key: string) => {
  return _getCookie(key);
};

export const removeCookieData = (key: string): boolean => {
  if (key) {
    cookies.remove(key, cookieOptions);
    return true;
  }
  return false;
};

export const storeDataToCookie = (key: string, data: object | string | null): boolean => {
  if (data && key) {
    if (typeof data === "object") {
      _setCookie(key, JSON.stringify(data));
    } else {
      _setCookie(key, data);
    }
    return true;
  }
  return false;
};

export const getCurrentCookieUser = (): IUserCookieModel | null => {
  const data = _getCookie(CookieEnum.User);

  try {
    if (data === undefined) {
      return null;
    }
    if (data instanceof Object) {
      return new IUserCookieModel(data);
    } else {
      return null;
    }
  } catch {
    return null;
  }
};

export const getCurrentCookieToken = (): string | null => {
  const data = _getCookie(CookieEnum.Token);
  return data;
};

export const getCurrentCookieUserProp = (prop: string): string | null => {
  const data = _getCookie(CookieEnum.User);
  try {
    if (data instanceof Object) {
      const descriptor = Object.getOwnPropertyDescriptor(data, prop);
      return descriptor ? descriptor.value : null;
    } else {
      return null;
    }
  } catch {
    return null;
  }
};

export const getCookieLang = (): string | null => {
  return _getCookie(CookieEnum.Lang);
};

export const getCastelectReactAppVersion = (): string | null => {
  return _getCookie(CookieEnum.CastelectReactAppVersion);
};

export const clearAllApplication = (): void => {
  cookies.remove(CookieEnum.User, cookieOptions);
  cookies.remove(CookieEnum.Token, cookieOptions);
  cookies.remove(CookieEnum.Lang, cookieOptions);
  cookies.remove(CookieEnum.SelectedProfile, cookieOptions);
  cookies.remove(CookieEnum.LastLoggedUser, cookieOptions);
  cookies.remove(CookieEnum.InviteCode, cookieOptions);
  cookies.remove(CookieEnum.CastelectReactAppVersion, cookieOptions);
  // Session storage'ı temizleme
  sessionStorage.clear();
  // Local storage'ı temizleme
  localStorage.clear();
};
